import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';

import { createLogger } from '../utils/logger';

const logger = createLogger('expense');
export interface Expense {
  id?: number;
  title: string;
  amount: number;
  category: string;
  dueDate?: string;
  accountNumber?: string;
  provider?: string;
  notes?: string;
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
}

class ExpenseService {
  private async getAuthHeaders() {
    const token = await getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async storeExpense(expense: Omit<Expense, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<Expense> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_URL}/api/expenses`, {
        method: 'POST',
        headers,
        body: JSON.stringify(expense),
      });

      if (!response.ok) {
        throw new Error('Failed to store expense');
      }

      return await response.json();
    } catch (error) {
      logger.error('Error storing expense:', error);
      throw error;
    }
  }

  async getAllExpenses(): Promise<Expense[]> {
    try {
      const headers = await this.getAuthHeaders();
      
      // Add timeout to prevent infinite hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout (increased for AI operations)

      const response = await fetch(`${API_URL}/api/expenses`, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        logger.error('Failed to fetch expenses:', response.status, response.statusText);
        return [];
      }

      return await response.json();
    } catch (error: any) {
      logger.error('Error fetching expenses (using fallback):', error);
      if (error.name === 'AbortError') {
        logger.debug('Expense fetch timeout - using fallback');
      }
      return [];
    }
  }

  async getExpenseById(id: number): Promise<Expense | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_URL}/expenses/${id}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      logger.error('Error fetching expense:', error);
      return null;
    }
  }

  async updateExpense(id: number, expense: Partial<Expense>): Promise<Expense> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_URL}/api/expenses/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(expense),
      });

      if (!response.ok) {
        throw new Error('Failed to update expense');
      }

      return await response.json();
    } catch (error) {
      logger.error('Error updating expense:', error);
      throw error;
    }
  }

  async deleteExpense(id: number): Promise<boolean> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_URL}/api/expenses/${id}`, {
        method: 'DELETE',
        headers,
      });

      return response.ok;
    } catch (error) {
      logger.error('Error deleting expense:', error);
      return false;
    }
  }

  async getExpenseCount(): Promise<number> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_URL}/expenses/count`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        return 0;
      }

      const data = await response.json();
      return data.count || 0;
    } catch (error) {
      logger.error('Error fetching expense count:', error);
      return 0;
    }
  }
}

export const expenseService = new ExpenseService(); 